import { eTlfItemType } from "functions/api/enums";
import {
  isArsMasterReadDto,
  isTlfItemReadDto,
  TlfArsMasterReadDto,
} from "functions/api/tlfItem/TlfItemReadDto";
import { applyVariantPatches } from "functions/tlf/applyVariantPatches";
import { buildVariantPatch } from "functions/tlf/buildVariantPatch";
import { cloneDeep } from "lodash";
import { checkChanged } from "./checkChanged";
import { ITlfActionCreateVariantPatch, TlfContextProps } from "./interface";
import { toArsTlfSnapshot } from "./toArsTlfSnapshot";
import { updateVariantPatchSelection } from "./updateVariantPatchSelection";

/**
 * Promotes an ARS item if needed, commits the active patch, and creates a new active patch.
 *
 * @param state - Current TLF context state
 * @param action - New patch name
 * @returns Updated TLF context state, or the original state when creation is invalid
 */
export const tlf_createVariantPatch = (
  state: TlfContextProps,
  action: ITlfActionCreateVariantPatch,
): TlfContextProps => {
  const patchName = action.patchName.trim();
  if (state.readOnly || patchName === "") return state;

  let creationState = state;
  if (!state.variantCreationView) {
    const arsItem =
      isTlfItemReadDto(state.tlfData) &&
      (state.tlfData.type === eTlfItemType.arsTable ||
        state.tlfData.type === eTlfItemType.arsListing)
        ? state.tlfData
        : null;
    if (arsItem === null) return state;
    const promoted: TlfArsMasterReadDto = isArsMasterReadDto(arsItem)
      ? arsItem
      : { ...arsItem, allVariantPatches: {}, variantTemplates: [] };
    const master = state.masterTlfData ?? toArsTlfSnapshot(promoted);
    const current: TlfArsMasterReadDto = cloneDeep(
      state.appliedVariantPatches.length > 0
        ? promoted
        : {
            ...master,
            allVariantPatches: promoted.allVariantPatches,
            variantTemplates: promoted.variantTemplates,
          },
    );
    creationState = {
      ...state,
      variantCreationView: true,
      masterTlfData: cloneDeep(master),
      tlfData: current,
      tlfDataVersions: state.tlfDataVersions.map((version, index) =>
        index === state.ixTlfDataVersion ? cloneDeep(current) : version,
      ),
    };
  }

  const current = isArsMasterReadDto(creationState.tlfData) ? creationState.tlfData : null;
  const master = creationState.masterTlfData;
  if (current === null || master === null) return state;

  const catalog = { ...current.allVariantPatches };
  const activeName = creationState.activeVariantPatchName;
  if (activeName !== null) {
    const patchBaseline: TlfArsMasterReadDto = {
      ...master,
      allVariantPatches: catalog,
      variantTemplates: current.variantTemplates,
    };
    const base = applyVariantPatches(
      patchBaseline,
      { allVariantPatches: catalog, variantTemplates: current.variantTemplates },
      creationState.appliedVariantPatches.filter((name) => name !== activeName),
    );
    if (!base.success) return state;
    const activeResult = buildVariantPatch(base.data, current);
    if (activeResult.invalidOps.length > 0) return state;
    catalog[activeName] = activeResult.patch;
  }

  if (catalog[patchName] !== undefined) return state;
  catalog[patchName] = [];

  const tlfData: TlfArsMasterReadDto = { ...current, allVariantPatches: catalog };
  const ixTlfDataVersion = creationState.ixTlfDataVersion + 1;
  const newState: TlfContextProps = {
    ...creationState,
    tlfData,
    ixTlfDataVersion,
    ixSaveVersion:
      creationState.ixSaveVersion > creationState.ixTlfDataVersion
        ? -1
        : creationState.ixSaveVersion,
    tlfDataVersions: [
      ...creationState.tlfDataVersions.slice(0, ixTlfDataVersion),
      cloneDeep(tlfData),
    ],
    appliedVariantPatches: [...creationState.appliedVariantPatches, patchName],
    activeVariantPatchName: patchName,
    activeVariantPatchInvalidOps: [],
    showCreateVariantPatchWindow: false,
  };
  const selectedState = updateVariantPatchSelection(
    newState,
    newState.appliedVariantPatches,
    newState.activeVariantPatchName,
  );
  checkChanged(selectedState);
  return selectedState;
};
